// shared-types/src/storeTemplate/portable.ts
import type { StoreCustomization } from '../storeCustomization/types'

/** Builder/system page IDs that stay stable across accounts (not store_page UUIDs). */
export const SYSTEM_BUILDER_PAGE_IDS: ReadonlySet<string> = new Set([
    'home',
    'catalog',
    'product-detail',
    'contact',
])

/** Portable CMS page IDs in persisted store_template rows (avoid cross-account UUID leaks). */
export const TPL_PAGE_PREFIX = 'tpl-page:' as const

/**
 * Matches template path or web URL segment to a single canonical key (slug only, lowercase).
 * Example: "/Mi-Pagina/" -> "mi-pagina".
 */
export function slugKeyFromCustomizationPath(path: string | undefined | null): string {
    const s = String(path ?? '')
        .trim()
        .replace(/^\/+/g, '')
        .replace(/\/+$/g, '')
        .toLowerCase()
    return s === '' ? 'root' : s
}

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isTplPortablePageId(id: string): boolean {
    return id.startsWith(TPL_PAGE_PREFIX)
}

/** True when id is a probable store_page UUID (not system, not portable prefix). */
export function isLikelyLegacyCmsPageUuid(id: string): boolean {
    if (!id || SYSTEM_BUILDER_PAGE_IDS.has(id)) return false
    if (isTplPortablePageId(id)) return false
    return UUID_RE.test(id)
}

export function remapPageReferenceIdsDeep(obj: unknown, idMap: ReadonlyMap<string, string>): void {
    if (obj === null || obj === undefined || typeof obj !== 'object') return
    if (Array.isArray(obj)) {
        for (const entry of obj) remapPageReferenceIdsDeep(entry, idMap)
        return
    }
    const o = obj as Record<string, unknown>
    const ref = o.referenceId
    const pageRel =
        o.itemType === 'PAGE' || o.linkType === 'PAGE'
    if (typeof ref === 'string' && ref.length > 0 && pageRel && idMap.has(ref)) {
        o.referenceId = idMap.get(ref)!
    }
    for (const k of Object.keys(o)) {
        if (k === 'referenceId' && pageRel) continue
        remapPageReferenceIdsDeep(o[k], idMap)
    }
}

/** Deep clones layout + theme + pages (JSON round-trip). */
function cloneCustomizationSlice(config: Partial<Pick<StoreCustomization, 'layout' | 'pages' | 'theme'>>) {
    return JSON.parse(JSON.stringify(config)) as Partial<Pick<StoreCustomization, 'layout' | 'pages' | 'theme'>>
}

/**
 * Replaces CMS page IDs with portable `tpl-page:{slugKey}` IDs and rewires PAGE referenceIds (header, grids…).
 */
export function normalizeTemplateConfigForPersist(config: Partial<Pick<StoreCustomization, 'layout' | 'pages' | 'theme'>>) {
    if (!config?.pages || !Array.isArray(config.pages)) return config

    const next = cloneCustomizationSlice(config)
    const pages = next.pages!

    /** Map old customization page id -> portable id (for CMS pages only). */
    const exportMap = new Map<string, string>()

    for (const raw of pages) {
        const p = raw as { id?: string; path?: string }
        const oldId = typeof p?.id === 'string' ? p.id : ''
        if (!oldId || SYSTEM_BUILDER_PAGE_IDS.has(oldId)) continue

        const key = slugKeyFromCustomizationPath(p.path)
        const portableId = `${TPL_PAGE_PREFIX}${key}`
        if (oldId !== portableId) exportMap.set(oldId, portableId)
        p.id = portableId
    }

    if (exportMap.size > 0 && next.layout !== undefined && next.layout !== null) {
        remapPageReferenceIdsDeep(next.layout, exportMap)
    }

    return next
}

/** Local preview: remap portable UUIDs/refs to ids from the current customization `pages`. */
export function remapPortableTemplateForLocalPreview<
    T extends Partial<Pick<StoreCustomization, 'layout' | 'pages' | 'theme'>>,
>(config: T, localCustomizationPages: Array<{ id?: string; path?: string }>): T {
    if (!config.pages || !Array.isArray(config.pages)) return JSON.parse(JSON.stringify(config))

    const out = cloneCustomizationSlice(config as Partial<Pick<StoreCustomization, 'layout' | 'pages' | 'theme'>>) as T

    /** slugKey -> first matching local customization page id */
    const slugToLocalId = new Map<string, string>()
    for (const lp of localCustomizationPages) {
        if (typeof lp?.id !== 'string' || !lp.id || SYSTEM_BUILDER_PAGE_IDS.has(lp.id)) continue
        const k = slugKeyFromCustomizationPath(lp.path)
        if (!slugToLocalId.has(k)) slugToLocalId.set(k, lp.id)
    }

    const idMap = new Map<string, string>()

    const pagesArr = out.pages as Array<{ id?: string; path?: string }>
    for (const p of pagesArr) {
        const oldId = typeof p?.id === 'string' ? p.id : ''
        if (!oldId || SYSTEM_BUILDER_PAGE_IDS.has(oldId)) continue

        if (isTplPortablePageId(oldId)) {
            const key = oldId.slice(TPL_PAGE_PREFIX.length)
            const local = slugToLocalId.get(key)
            if (local) {
                idMap.set(oldId, local)
                p.id = local
            }
            continue
        }

        if (isLikelyLegacyCmsPageUuid(oldId)) {
            const k = slugKeyFromCustomizationPath(p.path)
            const local = slugToLocalId.get(k)
            if (local && local !== oldId) {
                idMap.set(oldId, local)
                p.id = local
            }
        }
    }

    if (idMap.size > 0 && out.layout !== undefined && out.layout !== null) {
        remapPageReferenceIdsDeep(out.layout, idMap)
    }

    return out
}
